import * as React from 'react';

/* eslint-disable react/prop-types */
import format from 'date-fns/format';
import { graphql, useStaticQuery } from 'gatsby';
import styled, { css } from 'styled-components';
import moment from 'moment';

import Tag from 'shared/SchedulePage/Tag';
import { formatClassTime, formatDayOfWeek } from 'utils/dateFormat';

import { Typography } from '../../../components/DesignSystemV2/Typography';
import { colorsV2 } from '../../../style/colors-v2';
import { fromScreen } from '../../../utils/media-query/responsive.util';
import { ResponsiveLayout } from '../../Layout/ResponsiveLayout';
import { withHomePageSection } from 'components/withHomePageSection';
import { EmptyJuniorSchedule } from 'components/EmptyJuniorSchedule';

const IsSeatAvailable = ({ status }) => {
  let color = colorsV2.green100;
  let bgColor = colorsV2.green10;

  if (status === 'Gần hết chỗ') {
    color = colorsV2.yellow100;
    bgColor = colorsV2.yellow10;
  }
  if (status === 'Hết chỗ') {
    color = colorsV2.primary100;
    bgColor = colorsV2.primary10;
  }

  return (
    <Tag color={bgColor} type="filled">
      <Typography variant="medium/14-20" style={{ color }}>
        {status || 'Còn chỗ'}
      </Typography>
    </Tag>
  );
};

export const TextHighlight = ({ color = '#FF991F', children, ...props }) => {
  return (
    <Typography
      variant="regular/14"
      style={{ color: colorsV2.black80, boxShadow: `0px 2px 0px ${color}` }}
      {...props}
    >
      {children}
    </Typography>
  );
};

export const toClassSchedule = (data, level) => {
  return data.allGoogleSpreadsheetDolLandingPageMarLevelData.nodes
    .filter(item => item.level === level && Boolean(item.classDate))
    .map(
      ({
        startDate,
        classDate,
        classStatus,
        classTime,
        scheduleBranchName
      }) => {
        return {
          dateOpening: format(new Date(startDate), 'dd/MM/yyyy'),
          dateOpeningMobile: format(new Date(startDate), 'dd/MM/yyyy'),
          dayOfWeek: formatDayOfWeek(classDate),
          classStatus: classStatus,
          duration: formatClassTime(classTime),
          scheduleBranchName: `DOL ${scheduleBranchName}`
        };
      }
    );
};

const HeadingContainer = styled.div`
  display: grid;
  gap: 16px;
  grid-template-columns: max-content max-content;
  align-items: baseline;
  margin-bottom: 24px;
`;

export const ClassScheduleOfLevelContent = styled.div`
  display: grid;
  .btn-cta {
    button {
      width: 100%;
    }
  }
`;

const Main = styled.div`
  .view-full-schedule-btn {
    background-color: ${colorsV2.white100};
    border: 2px solid ${colorsV2.primary100};
    box-sizing: border-box;
    border-radius: 8px;
  }
  ${fromScreen(776)} {
    .section-title {
      font-size: 24px;
      line-height: 32px;
    }
  }
`;

const TableCell = styled.div`
  padding: 18px 10px;
  font-family: 'Inter';
  font-style: normal;
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  text-align: center;
  .tag {
    padding: 2px 8px;
    font-weight: 500;
    width: max-content;
    border-radius: 8px;
  }

  ${props =>
    !props.isLastRow &&
    css`
      border-bottom: 1px solid ${colorsV2.gray40};
    `}
`;

const TableCellClazzInfo = styled(TableCell)`
  text-align: left !important;
  display: flex;
  flex-direction: column;
  justify-content: center;
`;

const TableCellHeader = styled(TableCell)`
  background: ${colorsV2.gray5};
  display: flex;
  align-items: center;
  font-weight: 600;
  text-align: left;
  &:not(:first-child) {
    justify-content: center;
    border-top-right-radius: 8px;
  }
  &:first-child {
    border-top-left-radius: 8px;
  }
  svg {
    width: 20px;
    height: 20px;
    margin-right: 4px;
  }
`;

const ScheduleTableWrapper = styled.div`
  border: 1px solid ${colorsV2.gray40};
  border-radius: 8px;

  display: grid;

  grid-template-columns:
    minmax(max-content, 1fr)
    minmax(min-content, 1fr)
    minmax(min-content, 1fr);

  .tablet-hidden {
    justify-content: center;
    display: none;
  }
  ${fromScreen(776)} {
    grid-template-columns:
      minmax(max-content, 1fr)
      minmax(min-content, 1fr)
      minmax(min-content, 1fr)
      minmax(min-content, 1fr)
      minmax(min-content, 1fr);
    .tablet-hidden {
      display: grid;
    }
  }
`;
export const ScheduleTable = ({ data, onlineCourse }) => {
  return (
    <ScheduleTableWrapper>
      <TableCellHeader>{onlineCourse ? 'Lớp' : 'Chi nhánh'} </TableCellHeader>
      <TableCellHeader className="tablet-hidden">Số lượng HV</TableCellHeader>
      <TableCellHeader>Khai giảng</TableCellHeader>
      <TableCellHeader className="tablet-hidden">Buổi học</TableCellHeader>
      <TableCellHeader>Giờ học</TableCellHeader>

      {data.map(
        (
          {
            classStatus,
            scheduleBranchName,
            dateOpening,
            dateOpeningMobile,
            dayOfWeek,
            duration
          },
          index
        ) => (
          <React.Fragment key={index}>
            <TableCellClazzInfo
              key={index}
              status={classStatus}
              isLastRow={index === data.length - 1}
            >
              <span>
                <Typography variant="semi-bold/14-20" type="span">
                  {scheduleBranchName}
                </Typography>
              </span>
            </TableCellClazzInfo>
            <TableCell
              key={index}
              isLastRow={index === data.length - 1}
              className="tablet-hidden"
            >
              <IsSeatAvailable status={classStatus} />
            </TableCell>
            <TableCell
              key={index}
              status={classStatus}
              isLastRow={index === data.length - 1}
            >
              <ResponsiveLayout
                breakpointWithComponents={[
                  {
                    breakpoint: 775,
                    renderComponent: (
                      <>
                        {dateOpeningMobile}
                        <IsSeatAvailable status={classStatus} />
                      </>
                    )
                  },
                  {
                    breakpoint: 776,
                    ssrDefault: true,
                    renderComponent: dateOpening
                  }
                ]}
              />
            </TableCell>
            <TableCell
              key={index}
              status={classStatus}
              isLastRow={index === data.length - 1}
              className="tablet-hidden"
            >
              {dayOfWeek}
            </TableCell>
            <TableCell
              key={index}
              status={classStatus}
              isLastRow={index === data.length - 1}
            >
              <ResponsiveLayout
                breakpointWithComponents={[
                  {
                    breakpoint: 775,
                    renderComponent: (
                      <>
                        {dayOfWeek}
                        {duration}
                      </>
                    )
                  },
                  {
                    breakpoint: 776,
                    ssrDefault: true,
                    renderComponent: duration
                  }
                ]}
              />
            </TableCell>
          </React.Fragment>
        )
      )}
    </ScheduleTableWrapper>
  );
};

export const ClassScheduleOfLevel = withHomePageSection(
  ({ level }) => {
    const data = toClassSchedule(
      useStaticQuery(
        graphql`
          query {
            allGoogleSpreadsheetDolLandingPageMarLevelData(
              filter: { classListing: { eq: "listed" } }
            ) {
              nodes {
                level
                classStatus
                classDate
                classTime
                startDate
                scheduleBranchName
              }
            }
          }
        `
      ),
      level.level
    );

    const dataSort = data.sort((a, b) => {
      const dateA = moment(a.dateOpening, 'DD/MM/YYYY');
      const dateB = moment(b.dateOpening, 'DD/MM/YYYY');
      return dateA > dateB ? 1 : -1;
    });
    const isJunior = level.classGroup.includes('Junior');

    if (level.level && data.length > 0) {
      return (
        <Main id="class-schedule-of-level">
          <HeadingContainer>
            <Typography
              variant="bold/20-28"
              desktopVariant="bold/24-32"
              className="section-title"
              v3
              as="h2"
            >{`Lịch học ${level.levelDisplay}`}</Typography>
          </HeadingContainer>
          <ClassScheduleOfLevelContent>
            <ScheduleTable data={dataSort} />
          </ClassScheduleOfLevelContent>
        </Main>
      );
    } else if (level.level && data.length === 0 && isJunior) {
      return (
        <Main id="class-schedule-of-level">
          <HeadingContainer>
            <Typography
              variant="bold/20-28"
              desktopVariant="bold/24-32"
              className="section-title"
              v3
              as="h2"
            >{`Lịch học ${level.levelDisplay}`}</Typography>
          </HeadingContainer>
          <EmptyJuniorSchedule />
        </Main>
      );
    }
    return null;
  },
  { selfish: true }
);
